import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, CheckCircle2, HelpCircle, ChevronRight, RefreshCw } from 'lucide-react';
import api from '@/lib/api';

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const hoursAgo = (Date.now() - t) / (1000 * 3600);
    if (hoursAgo < 1) return `${Math.round(hoursAgo * 60)}m ago`;
    if (hoursAgo < 24) return `${Math.round(hoursAgo)}h ago`;
    return `${(hoursAgo / 24).toFixed(1)}d ago`;
  } catch { return iso; }
}

function Stat({ label, value, Icon, cls, testId }) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-md border ${cls}`} data-testid={testId}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="flex flex-col">
        <span className="text-lg font-semibold leading-none tabular-nums">{value}</span>
        <span className="text-[10px] leading-tight opacity-80">{label}</span>
      </div>
    </div>
  );
}

/**
 * Manager Visibility Widget — Day 3
 * Renders scope-aware compliance summary. When `clinical_chart_alerts_enabled`
 * is OFF, the backend returns `enabled: false` and this component renders nothing.
 * Re-fetches on mount; refresh button lets managers pull fresh data.
 */
export function BowelChartComplianceWidget({ participantId = null }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setErr(null);
      const res = await api.get('/participants/charts/compliance-summary');
      setSummary(res?.data?.data || null);
    } catch (e) {
      // Flag off (FEATURE_DISABLED) or forbidden → silently hide
      if (e?.response?.status === 403) {
        setSummary(null);
      } else {
        setErr(e?.response?.data?.error?.message || 'Failed to load compliance');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, []);

  if (loading) return null; // stay out of the way during first load
  if (err) return null;     // fail soft — never block the page
  if (!summary || summary.enabled === false) return null;

  const c = summary.counts || {};
  const needs = summary.needs_attention || [];

  // Limit the widget to the current participant's rows if a participantId is provided
  const scoped = participantId
    ? needs.filter(n => n.participant_id === participantId)
    : needs;

  return (
    <Card data-testid="compliance-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-1.5">
            Bowel chart compliance
            <Badge variant="outline" className="text-[10px]">{summary.total_active_bowel_charts || 0} charts</Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={fetchSummary} data-testid="compliance-widget-refresh">
            <RefreshCw className="w-3 h-3 mr-1" />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Compliant"  value={c.compliant ?? 0}  Icon={CheckCircle2}  cls="bg-emerald-50 border-emerald-200 text-emerald-700" testId="compliance-stat-compliant" />
          <Stat label="Due soon"   value={c.due_soon ?? 0}   Icon={Clock}         cls="bg-amber-50 border-amber-200 text-amber-700" testId="compliance-stat-due-soon" />
          <Stat label="Overdue"    value={c.overdue ?? 0}    Icon={AlertTriangle} cls="bg-rose-50 border-rose-200 text-rose-700" testId="compliance-stat-overdue" />
          <Stat label="No entries" value={c.no_entries ?? 0} Icon={HelpCircle}    cls="bg-slate-50 border-slate-200 text-slate-600" testId="compliance-stat-no-entries" />
        </div>
        {scoped.length > 0 && (
          <div className="space-y-1" data-testid="compliance-widget-attention">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Needs attention</div>
            {scoped.map(n => (
              <button
                key={n.chart_id}
                onClick={() => navigate(`/participants/${n.participant_id}/charts/${n.chart_id}`)}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-muted/60 text-left text-xs"
                data-testid={`compliance-attention-${n.chart_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{n.participant_name || '—'}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {n.chart_name || 'Bowel chart'} · last entry {formatRelative(n.last_entry_at)}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] bg-rose-50 border-rose-300 text-rose-700 shrink-0">
                  {n.overdue_by_hours != null ? `${n.overdue_by_hours.toFixed(0)}h over` : 'Overdue'}
                </Badge>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
